/**
 * "Can the crew app reach this person with a push?" — for every crew member
 * of one production, in one call.
 *
 * Goes through the get_crew_push_status RPC (migration 20261021140000). The
 * producer pages used to read push_subscriptions directly, which meant every
 * producer could read their crew's raw Expo tokens — and an Expo token is all
 * it takes to push someone anything. The RPC answers the one question the page
 * has and never returns a token. It also answers for active co-producers and
 * counts crew-app registrations only: a producer-app token says nothing about
 * whether the crew app can reach someone.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Client = any; // kept loose so both web and native Supabase clients work

export interface CrewPushStatusRow {
  user_id: string;
  has_crew_push: boolean;
}

/** The user ids whose crew app has a push registration. */
export function crewPushUserIds(rows: CrewPushStatusRow[] | null | undefined): Set<string> {
  return new Set((rows ?? []).filter((row) => row.has_crew_push).map((row) => row.user_id));
}

/**
 * Fetch the crew members of `briefingId` the crew app can push to.
 *
 * Fails soft — an error reads as "nobody has push". This feeds a status badge,
 * not a decision, and the page must not break while the database and the web
 * app are deployed a few minutes apart (the RPC may not exist yet).
 */
export async function fetchCrewPushUserIds(
  client: Client,
  briefingId: string | null | undefined,
): Promise<Set<string>> {
  if (!briefingId) return new Set();
  try {
    const { data, error } = await client.rpc("get_crew_push_status", {
      p_briefing_id: briefingId,
    });
    if (error) return new Set();
    return crewPushUserIds(data as CrewPushStatusRow[] | null);
  } catch {
    return new Set();
  }
}
